import {
  NotificationDisplayType,
  NotificationVariant,
} from '@/redux/types/notifications'
import React, { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { cn } from '@/lib/utils'

interface NotificationPopupProps {
  title: string
  message: string
  displayType?: NotificationDisplayType
  onClose: () => void
  type?: NotificationVariant
}

const NotificationPopup: React.FC<NotificationPopupProps> = ({
  title,
  message,
  displayType,
  onClose,
  type = 'info',
}) => {
  const popupRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const isCenter =
    displayType === NotificationDisplayType.MODAL_CENTER_AUTO_DISMISS

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 10)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [])

  const handleClose = () => {
    if (isCenter) {
      setIsClosing(true)
      setTimeout(() => {
        onClose()
      }, 200) // Match animation duration
    } else {
      onClose()
    }
  }

  const getIcon = () => {
    const iconSize = 'w-5 h-5'
    const iconContainerClasses = isCenter
      ? 'p-3 rounded-full flex items-center justify-center'
      : ''

    const icon = (() => {
      switch (type) {
        case 'success':
          return (
            <img src='/icons/success-icon.svg' alt='' className={iconSize} />
          )
        case 'error':
          return <img src='/icons/error-icon.svg' alt='' className={iconSize} />
        case 'warning':
          return (
            <img src='/icons/warning-icon.svg' alt='' className={iconSize} />
          )
        default:
          return <img src='/icons/info-icon.svg' alt='' className={iconSize} />
      }
    })()

    if (isCenter) {
      const bgColor = (() => {
        switch (type) {
          case 'success':
            return 'bg-green-100 text-green-600'
          case 'error':
            return 'bg-red-100 text-red-600'
          case 'warning':
            return 'bg-yellow-100 text-yellow-600'
          default:
            return 'bg-blue-100 text-blue-600'
        }
      })()

      return <div className={cn(iconContainerClasses, bgColor)}>{icon}</div>
    }

    return icon
  }

  const getVariant = () => {
    switch (type) {
      case 'success':
        return 'default'
      case 'error':
        return 'destructive'
      case 'warning':
        return 'default'
      default:
        return 'default'
    }
  }

  const getColorClasses = () => {
    if (isCenter) {
      return 'bg-white border-gray-200 text-gray-900 shadow-2xl'
    }

    const baseClasses = 'bg-white'
    switch (type) {
      case 'success':
        return `border-green-500 ${baseClasses} text-green-800`
      case 'error':
        return `border-red-500 ${baseClasses} text-red-800`
      case 'warning':
        return `border-yellow-500 ${baseClasses} text-yellow-800`
      default:
        return `border-blue-500 ${baseClasses} text-blue-800`
    }
  }

  const positionClasses = isCenter
    ? 'fixed inset-0 z-[9999] flex items-center justify-center p-4'
    : ''

  const containerClasses = isCenter
    ? 'w-full max-w-md'
    : 'w-full'

  const getAnimationClasses = () => {
    if (isCenter) {
      const baseClasses = 'transition-all duration-200 ease-out'
      if (isClosing) {
        return `${baseClasses} opacity-0 scale-95 translate-y-2`
      }
      return isVisible
        ? `${baseClasses} opacity-100 scale-100 translate-y-0`
        : `${baseClasses} opacity-0 scale-95 translate-y-2`
    }
    return 'duration-300 ease-out animate-in slide-in-from-bottom-2'
  }

  const getBackdropClasses = () => {
    if (!isCenter) return ''

    const baseClasses =
      'fixed inset-0 bg-black/40 backdrop-blur-sm z-[9998] transition-all duration-200'
    if (isClosing) {
      return `${baseClasses} opacity-0`
    }
    return isVisible ? `${baseClasses} opacity-100` : `${baseClasses} opacity-0`
  }

  return (
    <>
      {isCenter && (
        <div className={getBackdropClasses()} onClick={handleClose} />
      )}

      <div
        ref={popupRef}
        className={cn(
          positionClasses,
          getAnimationClasses(),
          isCenter ? '' : containerClasses
        )}
        role='alert'
        aria-live='assertive'
        aria-atomic='true'
      >
        <div className={isCenter ? 'w-full max-w-md' : ''}>
          <Alert
            variant={getVariant()}
            className={cn(
              isCenter ? 'border-0 rounded-xl' : 'border-2',
              'shadow-lg overflow-hidden relative',
              getColorClasses(),
              isCenter ? 'p-8' : ''
            )}
          >
            <div
              className={cn('flex gap-3 items-start', isCenter ? 'gap-6' : '')}
            >
              <div
                className={cn('flex-shrink-0', isCenter ? 'mt-0' : 'mt-0.5')}
              >
                {getIcon()}
              </div>

              <div className='flex-1 min-w-0'>
                <AlertTitle
                  className={cn(
                    'font-semibold leading-tight',
                    isCenter ? 'text-lg text-gray-900' : 'text-sm mb-1'
                  )}
                >
                  {title}
                </AlertTitle>
              </div>

              <Button
                variant='ghost'
                size='sm'
                onClick={handleClose}
                className={cn(
                  'flex-shrink-0 p-0 transition-all duration-200',
                  isCenter
                    ? 'w-10 h-10 hover:bg-gray-100 rounded-lg opacity-60 hover:opacity-100'
                    : 'w-6 h-6 opacity-70 hover:opacity-100'
                )}
                aria-label='Close notification'
              >
                <img src='/icons/close-icon.svg' alt='' className={'w-5 h-5'} />
              </Button>
            </div>
            <AlertDescription
              className={cn(
                'leading-relaxed',
                isCenter ? 'text-base text-gray-600 ' : 'text-sm'
              )}
            >
              {message}
            </AlertDescription>

            {isCenter && (
              <div
                className={cn(
                  'absolute bottom-0 left-0 right-0 h-1 rounded-b-xl',
                  type === 'success' ? 'bg-green-500' : '',
                  type === 'error' ? 'bg-red-500' : '',
                  type === 'warning' ? 'bg-yellow-500' : '',
                  type === 'info' ? 'bg-blue-500' : 'bg-blue-500'
                )}
              />
            )}
          </Alert>
        </div>
      </div>
    </>
  )
}

export default NotificationPopup
